import type { LateCancelCountsDto, LateCancelListStatus } from '@shared/core/cancellation-policy'
import { Link } from 'react-router-dom'
import {
  Loader,
  PageWrapper,
  Pagination,
  SegmentedControl,
  SegmentedControlOption,
  Typography,
} from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { LateCancelsEmptyState } from '../../components/late-cancels-empty-state'
import { LateCancelsList } from '../../components/late-cancels-list'
import { pickLateCancelsEmptyState } from '../../utils/late-cancel-display.util'
import classes from './late-cancels-wrapper.module.scss'
import { LateCancelsWrapperState, useLateCancelsWrapper } from './use-late-cancels-wrapper'

const OTHER_SETTINGS_PATH = `${routeNames.settings.path}?tab=other-settings`
const INTRO =
  'Clients who cancelled inside your cancellation window. Use this list to charge a fee or follow up, then mark each one resolved.'

function statusOptions(counts: LateCancelCountsDto | undefined): SegmentedControlOption<LateCancelListStatus>[] {
  return [
    { value: 'open', label: 'Open', count: counts?.open ?? 0 },
    { value: 'resolved', label: 'Resolved', count: counts?.resolved ?? 0 },
  ]
}

function LateCancelsContent({ page }: { page: LateCancelsWrapperState }) {
  if (page.isError) {
    return (
      <Typography variant="body" className={classes.error}>
        {"Couldn't load your late cancels. Please refresh the page."}
      </Typography>
    )
  }
  if (page.isLoading || !page.counts || !page.list) return <Loader isLoading={true} />
  if (page.list.items.length === 0) {
    return (
      <LateCancelsEmptyState
        kind={pickLateCancelsEmptyState(page.counts, page.status)}
        settingsPath={OTHER_SETTINGS_PATH}
      />
    )
  }
  return (
    <LateCancelsList
      records={page.list.items}
      pendingId={page.pendingId}
      onResolve={(record) => void page.markResolved(record)}
      onUndo={page.undo}
    />
  )
}

export function LateCancelsWrapper() {
  const page = useLateCancelsWrapper()

  return (
    <PageWrapper title="Late cancels" className={classes.page} authInfo>
      <div className={classes.root}>
        <div className={classes.intro}>
          <Typography variant="body" className={classes.description}>
            {INTRO}
          </Typography>
          <Link to={OTHER_SETTINGS_PATH} className={classes.settingsLink}>
            Cancellation policy settings <span aria-hidden="true">→</span>
          </Link>
        </div>
        <SegmentedControl
          ariaLabel="Filter late cancels"
          options={statusOptions(page.counts)}
          value={page.status}
          onChange={page.changeStatus}
        />
        <LateCancelsContent page={page} />
        {page.numberOfPages > 1 && <Pagination />}
      </div>
    </PageWrapper>
  )
}
